import React, { useEffect, useRef, useState } from "react";
import CommonTable from "components/ReactTable/Table";
// reactstrap components
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Input,
  Row,
  Col,
  Container,
  Badge,
} from "reactstrap";
import Select, { components } from "react-select";
import { useDispatch, useSelector } from "react-redux";
import {
  DEFAULT_RECORDS_PER_PAGE,
  fixedWidthCustomStyle,
  QUOTATION_STATUS_OPTIONS,
  WORK_ORDER,
  SMALL_CASE_WORK_ORDER,
} from "components/Common/constants";
import { getCustomerQuotationList } from "actions/customerQuotation";
import { customerQuotationList } from "constants/customerQuotation";
import NotificationAlert from "react-notification-alert";
import { useNavigate } from "react-router-dom";
import { urlEncodedId } from "components/Common/encodeDecodeFunction";
import { formatSAR } from "components/Common/commonFunctions";
import { handleQuotationStatus } from "./QuotationActions";

export const getStatusColor = (status) => {
  switch (status) {
    case "QUOTATION_APPROVED":
      return {
        backgroundColor: "#DCFCE7", // Light Green
        color: "#166534", // Dark Green
      };

    case "QUOTATION_REQUESTED_FOR_APPROVAL":
      return {
        backgroundColor: "#FEF3C7", // Light Yellow
        color: "#92400E", // Dark Yellow
      };

    case "QUOTATION_REVISED":
      return {
        backgroundColor: "#DBEAFE", // Light Blue
        color: "#1E40AF", // Dark Blue
      };

    case "QUOTATION_REJECTED":
      return {
        backgroundColor: "#FEE2E2", // Light Red
        color: "#991B1B", // Dark Red
      };

    default:
      return {
        backgroundColor: "#F3F4F6", // Light Gray
        color: "#374151", // Dark Gray
      };
  }
};

function QuotationListing() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const customerQuotationListData = useSelector(
    (state) => state.customerQuotation.customerQuotationList,
  );
  const rejectReasonRef = useRef("");
  const notificationAlertRef = useRef(null);
  const [quotationListState, setQuotationListState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
  });
  const [filterState, setFilterState] = useState({
    searchJob: "",
    searchStatus: null,
    filterParams: [],
  });
  const [showLoaderOnClick, setShowLoaderOnClick] = useState(false);
  const [pageNum, setPageNum] = useState(1);
  const [isAPiProcessing, setIsAPiProcessing] = useState(false);

  //table data
  const formattedQuotationList =
    customerQuotationListData?.data?.Quotation?.map((item, index) => ({
      ...item,
      srNo:
        (pageNum - 1) *
        (customerQuotationListData?.data?.noOfRecordsPerPage ||
          DEFAULT_RECORDS_PER_PAGE) +
        index +
        1,
    })) || [];

  useEffect(() => {
    getCustomerQuotationList(dispatch, pageNum, filterState.filterParams);
    return () => {
      dispatch({ type: customerQuotationList, payload: {} });
    };
  }, [dispatch, pageNum, filterState.filterParams]);

  //to check the customer quotation list response and till the time show loader or error as per response
  useEffect(() => {
    if (customerQuotationListData?.code === 200) {
      setQuotationListState({
        isLoading: false,
        error: "",
        noRecords: customerQuotationListData?.data?.Quotation?.length === 0,
      });
    } else if (
      customerQuotationListData?.code ||
      customerQuotationListData?.message
    ) {
      setQuotationListState({
        isLoading: false,
        error:
          customerQuotationListData?.message ||
          "Failed to load quotation list! Please try again.",
        noRecords: false,
      });
    }
  }, [customerQuotationListData]);

  const jobDetailsNavigate = (rowData) => {
    const encodedId = urlEncodedId(rowData.jobID);
    navigate(`/customer/jobs/${encodedId}/details`);
  };

  const columns = [
    {
      Header: "Sr. No.",
      accessor: "srNo",
      className: "col-srNo",
    },
    {
      Header: "Quotation ID",
      accessor: "ID",
      className: "col-quotation",
    },
    {
      Header: `${WORK_ORDER} No.`,
      accessor: "jobID",
      className: "col-quot-work-no",
      Cell: ({ row }) =>
      (
        <span
          className="cell-link"
          onClick={(e) => {
            e.stopPropagation();
            jobDetailsNavigate(row.original)
          }}
        >
          {row.original.jobID}
        </span>
      ),
    },
    {
      Header: "Outlet Name",
      accessor: "customerLocation",
      className: "col-outlet",
      Cell: ({ row, value }) => <span>{row?.original?.customerLocation?.outletName}</span>
    },
    {
      Header: "Outlet Address",
      accessor: "updatedOn",
      className: "col-outlet-address",
      Cell: ({ row, value }) => <span>{row?.original?.customerLocation?.address}</span>
    },
    {
      Header: "Grand Total",
      accessor: "grandTotal",
      className: "col-grand-total",
      Cell: ({ value }) => `SAR ${formatSAR(value || 0)}`,
    },
    {
      Header: "Status",
      accessor: "quotationStatus",
      className: "col-quot-status",
      Cell: ({ value }) => (
        <Badge color="" pill style={getStatusColor(value)}>
          {
            value.replace("QUOTATION_", "") //remove quotation with ""
          }
        </Badge>
      ),
    },
    {
      Header: "Created At",
      accessor: "createdAt",
      className: "col-created-at",
    },
    {
      Header: "Action",
      accessor: "action",
      className: "col-quot-actions",
      disableSortBy: true,
      Cell: ({ row }) => {
        const { quotationStatus, ID, jobID } = row.original;

        return quotationStatus === "QUOTATION_REQUESTED_FOR_APPROVAL" ? (
          <div className="actions-right d-flex justify-content-start gap-2">
            <div
              className="quotation-btn quotation-btn-accept"
              onClick={(e) => {
                e.stopPropagation();

                handleQuotationStatus({
                  type: "accept",
                  quotationId: ID,
                  jobId: jobID,
                  notify,
                  setIsAPiProcessing,
                  rejectReasonRef,
                  onSuccess: () =>
                    getCustomerQuotationList(dispatch, pageNum, filterState.filterParams)
                });
              }}
            >
              <i className="fa fa-check mr-1" />
              Accept
            </div>

            <div
              className="quotation-btn quotation-btn-reject"
              onClick={(e) => {
                e.stopPropagation();

                handleQuotationStatus({
                  type: "reject",
                  quotationId: ID,
                  jobId: jobID,
                  notify,
                  setIsAPiProcessing,
                  rejectReasonRef,
                  onSuccess: () =>
                    getCustomerQuotationList(dispatch, pageNum, filterState.filterParams)
                });
              }}
            >
              <i className="fa fa-times mr-1" />
              Reject
            </div>
          </div>
        ) : (
          <div className="text-left">-</div>
        );
      },
    },
  ];

  const notify = (type, message) => {
    const options = {
      place: "tr",
      message: <div>{message}</div>,
      type,
      icon: "tim-icons icon-bell-55",
      autoDismiss: 5,
    };
    notificationAlertRef.current.notificationAlert(options);
  };

  const handleSearch = () => {
    const searchString = filterState.searchJob.trim();
    if (!searchString && !filterState.searchStatus) {
      return;
    }
    const newParams = buildFilterParams();
    if (
      JSON.stringify(filterState.filterParams) === JSON.stringify(newParams)
    ) {
      return;
    }
    setQuotationListState((prev) => ({
      ...prev,
      isLoading: true,
      error: "",
      noRecords: false,
    }));

    setShowLoaderOnClick(true);

    setFilterState((prev) => ({
      ...prev,
      filterParams: newParams,
    }));

    setPageNum(1);
    setTimeout(() => {
      setShowLoaderOnClick(false);
    }, 600);
  };

  const handleClear = () => {
    setQuotationListState((prev) => ({
      ...prev,
      isLoading: true,
      error: "",
      noRecords: false,
    }));
    setFilterState({
      searchJob: "",
      searchStatus: null,
      filterParams: [],
    });
    if (pageNum !== 1) setPageNum(1);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const buildFilterParams = () => {
    const filterParams = [];

    if (filterState.searchJob.trim()) {
      filterParams.push({
        field: "JobID",
        condition: "eq",
        filterValues: [filterState.searchJob.trim()],
      });
    }

    if (filterState.searchStatus) {
      filterParams.push({
        field: "QuotationStatus",
        condition: "eq",
        filterValues: [filterState.searchStatus.value],
      });
    }

    return filterParams.length > 0 ? JSON.stringify(filterParams) : [];
  };

  // Custom Option Renderer
  const Option = (props) => {
    return (
      <components.Option {...props}>
        <Badge color="" pill style={getStatusColor(props.data.value)}>
          {props.data.label}
        </Badge>
      </components.Option>
    );
  };

  const SingleValue = (props) => (
    <components.SingleValue {...props}>
      <Badge color="" pill style={getStatusColor(props.data.value)}>
        {props.data.label}
      </Badge>
    </components.SingleValue>
  );

  //row click
  const handleRowClick = (rowData) => {
    navigate(`/customer/quotations/${urlEncodedId(rowData.jobID)}/details`);
  };

  return (
    <>
      {isAPiProcessing && (
        <div className="loading-overlay">
          <div className="loading-card">
            <i className="fa fa-spinner fa-spin fa-2x text-deep-blue mb-3" />
            <div className="font-weight-bold text-dark">
              Processing… Please wait.
            </div>
          </div>
        </div>
      )}
      <div className="rna-container">
        <NotificationAlert ref={notificationAlertRef} />
      </div>
      <Container fluid className="mt-4">
        <Row>
          <Col md="12">
            <Card>
              <CardHeader className="d-flex justify-content-between align-items-center">
                <CardTitle tag="h3" className="font-weight-normal">
                  Quotations
                </CardTitle>
              </CardHeader>

              <CardBody>
                <div className="d-flex align-items-center justify-content-between flex-wrap flex-column flex-md-row">
                  {/* Left side - Filter Inputs + Search */}
                  <div className="d-flex flex-wrap align-items-center filter-group">
                    <Input
                      type="text"
                      placeholder={`Search by ${SMALL_CASE_WORK_ORDER} no.`}
                      className="mr-2 mb-2 filter-input-large"
                      value={filterState.searchJob}
                      onChange={(e) =>
                        setFilterState((prev) => ({
                          ...prev,
                          searchJob: e.target.value,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                      maxLength={100}
                    />

                    <Select
                      className="mr-2 mb-2 filter-select"
                      options={QUOTATION_STATUS_OPTIONS}
                      placeholder="Status"
                      value={filterState.searchStatus}
                      onChange={(selected) =>
                        setFilterState((prev) => ({
                          ...prev,
                          searchStatus: selected,
                        }))
                      }
                      classNamePrefix="react-select"
                      styles={fixedWidthCustomStyle()}
                      components={{ Option, SingleValue }}
                      onKeyDown={handleKeyDown}
                    />

                    <Button
                      color="default"
                      className="d-flex justify-content-center align-items-center mb-3 filter-btn btn-simple mt-2 mr-1"
                      onClick={handleSearch}
                    >
                      {showLoaderOnClick ? (
                        <i className="fa fa-spinner fa-spin search-loader" />
                      ) : (
                        <>
                          <i className="fa fa-search mr-1" />
                          Search
                        </>
                      )}
                    </Button>

                    {Object.keys(filterState.filterParams).length > 0 && (
                      <Button
                        color="default"
                        className="mb-3 justify-content-center align-items-center mt-2 ml-1 filter-btn btn-simple"
                        onClick={handleClear}
                      >
                        <i className="fa fa-times mr-1" /> Clear
                      </Button>
                    )}
                  </div>
                </div>

                <div className="mt-2 mb-3 mx-1">
                  {customerQuotationListData?.data?.totalRecords ? (
                    filterState.searchJob.trim() || filterState.searchStatus ? (
                      <span className="font-weight-bold">
                        Showing{" "}
                        {customerQuotationListData?.data?.totalFilteredRecords}{" "}
                        of {customerQuotationListData?.data?.totalRecords}{" "}
                        Quotations
                      </span>
                    ) : (
                      <span className="font-weight-bold">
                        Total Quotations :{" "}
                        {customerQuotationListData?.data?.totalRecords}
                      </span>
                    )
                  ) : null}
                </div>
                {quotationListState.isLoading ? (
                  <div className="loaderContainer">
                    <i className="fa fa-spinner fa-spin fa-2xl loader" />
                  </div>
                ) : quotationListState.error ? (
                  <>
                    <div className="loaderContainer">
                      {quotationListState.error}
                    </div>
                  </>
                ) : quotationListState.noRecords ? (
                  <div className="loaderContainer text-muted">
                    No quotation added yet.
                  </div>
                ) : (
                  <div className="table-responsive">
                    <CommonTable
                      showPaginationBottom={true}
                      defaultPageSize={DEFAULT_RECORDS_PER_PAGE}
                      activePage={pageNum}
                      data={formattedQuotationList || []}
                      resizable={false}
                      columns={columns}
                      className="-striped -highlight table-hover"
                      rowClick={handleRowClick}
                      totalPages={Math.ceil(
                        customerQuotationListData?.data?.totalFilteredRecords /
                        customerQuotationListData?.data?.noOfRecordsPerPage ||
                        DEFAULT_RECORDS_PER_PAGE,
                      )}
                      setPageNum={setPageNum}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default QuotationListing;
